import { Home, Monitor, ClipboardList, ListOrdered, BadgeDollarSign, Bike, LockOpen, Printer, ArchiveRestore, Bell, Settings, Headset, ShieldCheck } from "lucide-react";
import { api, canSee, isManagerRole, todayISO } from "@/lib/api";

export const APP_VERSION = "1.0.0";
export const isNetErr = (e) => !e?.response;
export const sendPrint = (kind, title, payload) => api.post("/print-jobs", { kind, title, payload });
export const comandaTitle = (c) => `${c.table_number ? `Mesa ${c.table_number} · ` : ""}Comanda #${c.number}`;
export const pad2 = (n) => String(n).padStart(2, "0");
export const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
export const activeItems = (c) => (c?.items || []).filter((i) => i.status !== "cancelled");
export const isMine = (c, u) => (c.opened_by_id ? c.opened_by_id === u.id : c.opened_by === u.name);

// Empresa com snapshot do teste grátis: esconde o que o teste não libera (antigas e pagantes não recebem trial_modules)
export const trialOff = (u, m) => Array.isArray(u?.trial_modules) && !u.trial_modules.includes(m);
export const canSales = (u) => !trialOff(u, "pdv") && (isManagerRole(u) || canSee(u, "pdv"));
export const canMenuOrders = (u) => !trialOff(u, "delivery") && (isManagerRole(u) || canSee(u, "delivery"));
export const isDirect = (u, t) => !trialOff(u, "pdv") && (u?.direct_sale ?? !!t?.settings?.mobile_direct_sale);
export const MAIN_SCREENS = [
  { to: "/m", label: "Início", icon: Home, end: true },
  { to: "/m/pdv", label: "PDV", icon: Monitor },
  { to: "/m/comandas", label: "Comandas", icon: ClipboardList, show: (u, t) => !isDirect(u, t) },
  { to: "/m/pedidos", label: "Pedidos", icon: ListOrdered, show: (u, t) => !isDirect(u, t) },
  { to: "/m/vendas", label: "Vendas", icon: BadgeDollarSign, show: canSales },
  { to: "/m/delivery", label: "Delivery", icon: Bike, show: (u) => canSee(u, "delivery") },
  { to: "/m/liberacao", label: "Liberação", icon: LockOpen },
  { to: "/m/impressoes", label: "Impressões centralizadas", icon: Printer },
  { to: "/m/backup", label: "Backup de comandas", icon: ArchiveRestore },
];
export const CONFIG_SCREENS = [
  { to: "/m/notificacoes", label: "Histórico Notificações", icon: Bell },
  { to: "/m/config", label: "Configurações", icon: Settings },
  { to: "/m/suporte", label: "Suporte e Atendimento", icon: Headset },
  { to: "/m/privacidade", label: "Política de privacidade", icon: ShieldCheck },
];
export const screensFor = (u, list, t) => list.filter((s) => !s.show || s.show(u, t));

let scope = "";
export const setCacheScope = (s) => { scope = s; };
export async function cachedGet(url) {
  const key = `m_cache_${scope}_${url}`;
  try {
    const { data } = await api.get(url);
    try { localStorage.setItem(key, JSON.stringify(data)); } catch { /* armazenamento cheio */ }
    return data;
  } catch (e) {
    const saved = localStorage.getItem(key);
    if (isNetErr(e) && saved) return JSON.parse(saved);
    throw e;
  }
}

export const offlineComanda = (e) => ({
  id: e.temp_id, number: e.payload.number, customer_name: e.payload.customer_name, people: e.payload.people,
  table_number: e.table_number, items: [], orders: [], subtotal: 0, total: 0, remaining: 0, paid: 0, offline: true, error: e.error,
  opened_by: e.opened_by, opened_by_id: e.opened_by_id,
});
export const offlineComandas = (queue) => queue.filter((e) => e.type === "create").map(offlineComanda);

export function filterComandas(list, q) {
  const s = norm(q);
  if (!s) return list;
  return list.filter((c) => String(c.number).startsWith(s) || norm(c.customer_name).includes(s) || (c.table_number && `mesa ${c.table_number}`.includes(s)));
}

export function todaySales(list) {
  const day = todayISO();
  const today = list.filter((s) => new Date(s.created_at).toLocaleDateString("sv-SE") === day);
  const done = today.filter((s) => s.status === "completed");
  return { list: today, count: done.length, total: done.reduce((a, s) => a + s.total, 0) };
}

export function orderGroups(c) {
  const groups = (c.orders || []).map((o) => ({ order: o, items: c.items.filter((i) => i.order_id === o.id) })).filter((g) => g.items.length);
  const loose = c.items.filter((i) => !i.order_id || !(c.orders || []).some((o) => o.id === i.order_id));
  return loose.length ? [...groups, { order: null, items: loose }] : groups;
}

export const billPayload = (c) => ({
  comanda: {
    number: c.number, table_number: c.table_number, customer_name: c.customer_name,
    items: activeItems(c).map(({ name, qty, total }) => ({ name, qty, total })),
    subtotal: c.subtotal, discount: c.discount, service_fee: c.service_fee, surcharge: c.surcharge, total: c.total, paid: c.paid, remaining: c.remaining,
  },
});

export const reprintPayload = (c, o, items) => ({
  order: { number: o.number, at: o.at, user: o.user, pickup_number: o.pickup_number, label: comandaTitle(c), sectors: [],
    items: items.filter((i) => i.status !== "cancelled" && i.print !== false).map(({ name, qty, notes }) => ({ name, qty, notes: notes || "" })) },
  reprint: true,
});

export const ORDER_STATUS = { recebido: "Recebido", confirmado: "Confirmado", preparo: "Em preparo", pronto: "Pronto", entrega: "Saiu para entrega", entregue: "Entregue", cancelado: "Cancelado" };
export const ORDER_TYPES = { delivery: "Entrega", retirada: "Retirada", local: "No local", mesa: "Mesa", comanda: "Comanda" };
const FLOW = ["recebido", "confirmado", "preparo", "pronto", "entrega", "entregue"];
const ACTION = { confirmado: "Confirmar", preparo: "Em preparação", pronto: "Pronto para entrega", entrega: "Saiu para entrega", entregue: "Entregue" };
export function nextStatus(o, steps = {}) {
  const i = FLOW.indexOf(o.status);
  if (i < 0 || o.status === "entregue") return null;
  const s = FLOW.slice(i + 1).find((k) => k === "entregue" || (steps[k] !== false && (k !== "entrega" || o.type === "delivery")));
  return [s, s === "pronto" && o.type !== "delivery" ? (o.type === "retirada" ? "Pronto para retirada" : "Pronto") : ACTION[s]];
}
export const JOB_STATUS = { pending: ["Na fila", "amber"], printing: ["Imprimindo", "blue"], done: ["Impresso", "green"], failed: ["Falhou", "red"], expired: ["Não impresso", "red"] };
